import * as React from "react";
import { IconButton, Stack, Tooltip, useMediaQuery } from "@mui/material";
import NotesIcon from '@mui/icons-material/Notes';
import SelectedDateContainer from "./SelectedDateContainer";
import DetailsCardTab from "./DetailsCardTab";
import ReportButton from "../ReportButton";
import CustomExcelReportColumnDialog from "./CustomExcelReportColumnDialog";
import { createSetting } from "../../../services/settings.service";
import { PAGE_TYPE } from "../../../types/pages.type";
import { useDispatch, useSelector } from "react-redux";
import { setIsOpenNote } from "../../../store/reducers/common.slice";
import { CHART_TAB_BUTTON } from "../../../types/chart-dialog.type";

export default function ChartDetailsTab({
    queryFilters,
    selectedTab,
    handleTabButton,
    handleOnClickReport,
    handleOnClickShareReport,
    loading,
    handleSendAutomaticallyReport,
    handleExcelReport,
    tableFilterTypeOptions,
    setSelectedFacility,
    selectedFacility,
    questions,
    tableFilterType,
    tableFilterTypeSetting,
    pageType,
    subPageType,
    filterHeaderContainer,
    isMobile,
    // Optional: open Trend Mapping configuration from parent
    onOpenTrendMapping,
}) {
    const [isCustomerColumn, setIsCustomerColumn] = React.useState(false);
    const [selectedQuestions, setSelectedQuestions] = React.useState([]);
    const dispatch = useDispatch();
    const { isNotePopup } = useSelector((state) => state.common);
    const isMobileWidth = useMediaQuery('(max-width: 768px)');
    const isMobileLandscape = useMediaQuery('(max-height: 500px) and (max-width: 960px)');
    const isMobileDevice = isMobileWidth || isMobileLandscape;

    React.useEffect(() => {
        if (questions && questions.length > 0) {
            setSelectedQuestions(questions);
        }
        // eslint-disable-next-line
    }, [questions, pageType]);

    const handleSaveExcelColumn = React.useCallback((selectedColumns) => {
        createSetting({
            selectedColumns,
            selectedTab: tableFilterType || tableFilterTypeSetting,
            pageType: subPageType === PAGE_TYPE.ADT ? subPageType : pageType,
            ...subPageType === PAGE_TYPE.ADT && { subPageType }
        });
    }, [tableFilterType, pageType, tableFilterTypeSetting, subPageType]);

    const handleExcelReportCustom = React.useCallback((e) => {
        handleExcelReport(e);
        handleSaveExcelColumn(e)
    }, [handleExcelReport, handleSaveExcelColumn]);

    const handleOnclickOpenNotes = React.useCallback(() => {
        dispatch(setIsOpenNote(!isNotePopup));
    }, [isNotePopup, dispatch]);

    return (
        <>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={isMobileDevice ? 0.5 : 0}
                sx={{
                    ...(isMobileDevice && {
                        padding: '0 0',
                        flexWrap: 'wrap',
                        gap: '4px',
                        ml: 2
                    })
                }}
            >
                <Stack 
                    direction="row" 
                    spacing={isMobileDevice ? 0.5 : 1} 
                    sx={{ 
                        maxWidth: isMobileDevice ? "200px" : "400px", 
                        flexShrink: 0 
                    }}
                >
                    <DetailsCardTab
                        selectedTab={selectedTab}
                        handleTabButton={handleTabButton}
                        tableFilterTypeOptions={tableFilterTypeOptions}
                        setSelectedFacility={setSelectedFacility}
                        selectedFacility={selectedFacility}
                        isMobile={isMobileDevice}
                        onOpenTrendMapping={onOpenTrendMapping}
                    />
                </Stack>
                
                <Stack 
                    direction="row" 
                    spacing={isMobileDevice ? 0.5 : 1} 
                    sx={{ 
                        flexGrow: 1, 
                        ml: isMobileDevice ? "5px" : "10px",
                        ...(isMobileDevice && {
                            maxWidth: '100%',
                            overflow: 'hidden'
                        })
                    }}
                >
                    {filterHeaderContainer && filterHeaderContainer()}
                </Stack>
                <Stack sx={{ 
                    flexGrow: 0,
                    ...(isMobileDevice && {
                        mr:"35px !important",                      
                    })
                }}>
                    <Stack direction={"row"} spacing={isMobileDevice ? 0.5 : 1}>
                        {!loading && (
                            <Tooltip title={"Notes"} arrow>
                                <IconButton
                                    sx={{
                                        width: isMobileDevice ? 24 : 29,
                                        height: isMobileDevice ? 24 : 29,
                                        mr: isMobileDevice ? 0.5 : 1,
                                        mt: isMobileDevice ? "-4px !important" : "-6px",
                                        borderRadius: "50%",
                                        border: "1px solid grey",
                                        backgroundColor: "#fff",
                                        color: "primary.contrastText",
                                        ":hover": { backgroundColor: "grey", color: "#fff" },
                                        ...(isMobileDevice && {
                                            minWidth: '24px',
                                            minHeight: '24px'
                                        })
                                    }}
                                    disableFocusRipple={true}
                                    disableRipple={false}
                                    id="composition-button"
                                    aria-haspopup="true"
                                    onClick={handleOnclickOpenNotes}
                                >
                                    <NotesIcon sx={{
                                        fontSize: isMobileDevice ? 14 : 18,
                                        color: "#000"
                                    }} />
                                </IconButton>
                            </Tooltip>
                        )}


                        <ReportButton
                            handleSaveExcelColumn={handleSaveExcelColumn}
                            handleOnClickReport={handleOnClickReport}
                            handleOnClickShareReport={handleOnClickShareReport}
                            handleSendAutomaticallyReport={handleSendAutomaticallyReport}
                            loading={loading}
                            title={"report"}
                            sx={{ 
                                mt: isMobileDevice ? "-4px" : "0px", 
                                width: isMobileDevice ? 24 : 29, 
                                height: isMobileDevice ? 24 : 29,
                                ...(isMobileDevice && {
                                    minWidth: '24px',
                                    minHeight: '24px'
                                })
                            }}
                            selectedTab={selectedTab}
                            selectedTabSetting={tableFilterType || tableFilterTypeSetting}
                            handleExcelReport={handleExcelReport}
                            handleExcelReportCustom={() => { setIsCustomerColumn(true) }}
                            tableFilterType={tableFilterType}
                            selectedQuestions={selectedQuestions}
                            pageType={pageType}
                            subPageType={subPageType}
                            openFrom="card"
                            isCustomAlertEnable={selectedTab !== CHART_TAB_BUTTON.CHART}
                            isMobile={isMobileDevice}
                        />
                        <SelectedDateContainer 
                            queryFilters={queryFilters} 
                            isMobile={isMobileDevice}                            
                        />
                    </Stack>
                </Stack>
            </Stack>
            {isCustomerColumn &&
                <CustomExcelReportColumnDialog
                    pageType={pageType}
                    subPageType={subPageType}
                    selectedTab={tableFilterType || tableFilterTypeSetting}
                    selectedQuestions={selectedQuestions}
                    handleClose={() => setIsCustomerColumn(false)}
                    handleSave={(e) => {
                        setIsCustomerColumn(false);
                        handleExcelReportCustom(e)
                    }}                    
                />
            }
        </>
    );
}
